/**
 * Social-casino GEO groups from Google Ads / Octo scrape
 * (`BA-API-Handler/src/browser/cert_normalize.py`), converted to ISO-3166
 * alpha-2 via `buyer-journal/src/utils/geo_names.py`.
 *
 * Scrape expands "Group 1" / "Group 2" → country names → ISO rows.
 * UI collapses those ISO rows back into group chips.
 */

export const SOCIAL_CASINO_GROUP_1_ISO: readonly string[] = [
	"AT",
	"AU",
	"BR",
	"BG",
	"CA",
	"CH",
	"CO",
	"CZ",
	"DE",
	"DK",
	"ES",
	"FI",
	"FR",
	"GB",
	"GR",
	"HU",
	"IE",
	"IL",
	"JP",
	"LT",
	"LV",
	"MX",
	"MZ",
	"NG",
	"NL",
	"NO",
	"NZ",
	"PE",
	"PT",
	"RO",
	"RS",
	"SE",
	"SK",
	"US",
	"ZA",
] as const;

export const SOCIAL_CASINO_GROUP_2_ISO: readonly string[] = [
	"HK",
	"IN",
	"KR",
	"MY",
	"TH",
	"TW",
	"VN",
] as const;

export type SocialCasinoGroupId = "1" | "2";

export type MatchedSocialCasinoGroup = {
	id: SocialCasinoGroupId;
	label: string;
	/** ISO codes present on the account that belong to this group. */
	geos: string[];
};

/**
 * Collapse social-casino ISO codes back into Group 1 / Group 2.
 * Unmatched codes stay in `leftover` (plain country chips).
 */
export function matchSocialCasinoGroups(
	socialCasinoGeos: Iterable<string>,
): { groups: MatchedSocialCasinoGroup[]; leftover: string[] } {
	// Real scrape data is expected to expand "Group 1/2" into full ISO lists,
	// but due to mapping gaps we may receive a subset. UI should still treat
	// any subset of Group 1/2 as belonging to that group (not as leftover).
	const remaining = new Set(
		[...socialCasinoGeos].map((g) => g.trim().toUpperCase()).filter(Boolean),
	);

	const remainingList = [...remaining];
	const group1Set = new Set(SOCIAL_CASINO_GROUP_1_ISO);
	const group2Set = new Set(SOCIAL_CASINO_GROUP_2_ISO);

	const hits1 = remainingList
		.filter((iso) => group1Set.has(iso))
		.sort((a, b) => a.localeCompare(b));
	const hits2 = remainingList
		.filter((iso) => group2Set.has(iso))
		.sort((a, b) => a.localeCompare(b));

	const groups: MatchedSocialCasinoGroup[] = [];
	if (hits1.length) {
		groups.push({ id: "1", label: "Group 1", geos: hits1 });
	}
	if (hits2.length) {
		groups.push({ id: "2", label: "Group 2", geos: hits2 });
	}

	const groupUnion = new Set([...SOCIAL_CASINO_GROUP_1_ISO, ...SOCIAL_CASINO_GROUP_2_ISO]);
	const leftover = remainingList
		.filter((iso) => !groupUnion.has(iso))
		.sort((a, b) => a.localeCompare(b));

	return { groups, leftover };
}
